import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import SEO from "../../components/SEO";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export default function EmiCalculator() {
  const [loan, setLoan] = useState("");
  const [rate, setRate] = useState(8.5);
  const [tenure, setTenure] = useState("");
  const [emi, setEmi] = useState(null);
  const [error, setError] = useState("");
  const [loanType, setLoanType] = useState("home");

  const [schedule, setSchedule] = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (loanType === "home") setRate(8.5);
    if (loanType === "car") setRate(9.2);
    if (loanType === "personal") setRate(12);
  }, [loanType]);

  const validateInputs = () => {
    const P = Number(loan);
    const R = Number(rate);
    const N = Number(tenure);

    if (!loan || !rate || !tenure)
      return "All fields required";

    if (P <= 0 || N <= 0)
      return "Invalid values";

    if (R < 0)
      return "Rate cannot be negative";

    if (N > 600)
      return "Max tenure 600 months";

    return null;
  };

  const calculateEMI = () => {
    setError("");
    setEmi(null);
    setSchedule([]);

    const err = validateInputs();

    if (err) {
      setError(err);
      return;
    }

    const P = parseFloat(loan);
    const R = parseFloat(rate) / 12 / 100;
    const N = parseFloat(tenure);

    let EMIValue;

    if (R === 0) {
      EMIValue = P / N;
    } else {
      EMIValue =
        (P * R * Math.pow(1 + R, N)) /
        (Math.pow(1 + R, N) - 1);
    }

    const totalPayment = EMIValue * N;
    const totalInterest = totalPayment - P;

    let balance = P;
    let tempSchedule = [];

    for (let i = 1; i <= N; i++) {
      const interest = balance * R;
      const principal = EMIValue - interest;

      balance -= principal;

      tempSchedule.push({
        month: i,
        emi: EMIValue,
        principal,
        interest,
        balance: balance > 0 ? balance : 0,
      });
    }

    setSchedule(tempSchedule);

    setChartData([
      { name: "Principal", value: P },
      { name: "Interest", value: totalInterest },
    ]);

    setEmi(EMIValue.toFixed(2));
  };

  const downloadPDF = () => {
    const doc = new jsPDF();

    doc.text("EMI Schedule Report", 14, 10);

    const tableData = schedule.map((r) => [
      r.month,
      r.emi.toFixed(2),
      r.principal.toFixed(2),
      r.interest.toFixed(2),
      r.balance.toFixed(2),
    ]);

    autoTable(doc, {
      head: [["Month", "EMI", "Principal", "Interest", "Balance"]],
      body: tableData,
    });

    doc.save("emi-schedule.pdf");
  };

  return (
    <>
      <SEO
        title="EMI Calculator Online – Calculate Loan EMI Instantly"
        description="Free EMI Calculator to calculate home loan, car loan, and personal loan EMI with amortization schedule and PDF download."
        keywords="emi calculator, home loan emi calculator, car loan emi calculator, personal loan emi, loan calculator"
        url="https://online-tools-center.vercel.app/emi-calculator"
      />

      <Navbar />

      <div className="container py-4">

        <div className="row justify-content-center">

          <div className="col-lg-10">

            <div className="card shadow-lg border-0 rounded-4 p-4">

              <div className="text-center mb-4">

                <h1 className="fw-bold mb-3">
                  EMI Calculator
                </h1>

                <p className="text-muted">
                  Calculate Home Loan, Car Loan &
                  Personal Loan EMI instantly with
                  amortization schedule and PDF export.
                </p>

              </div>

              <div className="row g-4">

                <div className="col-md-6">

                  <label className="fw-semibold mb-2">
                    Loan Type
                  </label>

                  <select
                    className="form-select mb-3"
                    value={loanType}
                    onChange={(e) =>
                      setLoanType(e.target.value)
                    }
                  >
                    <option value="home">
                      Home Loan
                    </option>

                    <option value="car">
                      Car Loan
                    </option>

                    <option value="personal">
                      Personal Loan
                    </option>
                  </select>

                  <label className="fw-semibold mb-2">
                    Loan Amount
                  </label>

                  <input
                    type="number"
                    className="form-control mb-3"
                    placeholder="Enter Loan Amount"
                    value={loan}
                    onChange={(e) =>
                      setLoan(e.target.value)
                    }
                  />

                  <label className="fw-semibold mb-2">
                    Loan Tenure (Months)
                  </label>

                  <input
                    type="number"
                    className="form-control mb-3"
                    placeholder="Enter Loan Tenure"
                    value={tenure}
                    onChange={(e) =>
                      setTenure(e.target.value)
                    }
                  />

                  <div className="mb-4">

                    <label className="fw-semibold">
                      Interest Rate: {rate}%
                    </label>

                    <Slider
                      min={5}
                      max={20}
                      step={0.1}
                      value={rate}
                      onChange={(v) => setRate(v)}
                    />

                  </div>

                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold"
                    onClick={calculateEMI}
                  >
                    Calculate EMI
                  </button>

                  {error && (
                    <div className="alert alert-danger mt-3">
                      {error}
                    </div>
                  )}

                </div>

                <div className="col-md-6">

                  <div className="bg-light rounded-4 p-4 h-100">

                    <h4 className="fw-bold mb-3">
                      Why Use This EMI Calculator?
                    </h4>

                    <ul className="mb-4">
                      <li>
                        Instant EMI calculation
                      </li>

                      <li>
                        Accurate amortization schedule
                      </li>

                      <li>
                        PDF report download
                      </li>

                      <li>
                        Mobile-friendly calculator
                      </li>

                      <li>
                        Free and secure tool
                      </li>
                    </ul>

                    {emi ? (
                      <>
                        <div className="alert alert-success text-center">
                          <h5 className="mb-1">
                            Monthly EMI
                          </h5>

                          <h2 className="fw-bold">
                            ₹ {emi}
                          </h2>
                        </div>

                        <button
                          className="btn btn-dark w-100 mb-4"
                          onClick={downloadPDF}
                        >
                          Download PDF Report
                        </button>

                        <div
                          style={{
                            width: "100%",
                            height: 250,
                          }}
                        >
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie
                                data={chartData}
                                dataKey="value"
                                outerRadius={80}
                                label
                              >
                                <Cell fill="#4f46e5" />

                                <Cell fill="#f97316" />
                              </Pie>

                              <Tooltip />

                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">
                        Enter loan details to
                        calculate your EMI instantly.
                      </div>
                    )}

                  </div>

                </div>

              </div>

              {schedule.length > 0 && (
                <div className="mt-5">

                  <h3 className="fw-bold mb-3">
                    EMI Amortization Schedule
                  </h3>

                  <div
                    className="table-responsive"
                    style={{
                      maxHeight: "400px",
                    }}
                  >
                    <table className="table table-bordered table-striped">

                      <thead className="table-dark">

                        <tr>
                          <th>Month</th>
                          <th>EMI</th>
                          <th>Principal</th>
                          <th>Interest</th>
                          <th>Balance</th>
                        </tr>

                      </thead>

                      <tbody>

                        {schedule.map((row, i) => (
                          <tr key={i}>
                            <td>{row.month}</td>

                            <td>
                              ₹ {row.emi.toFixed(2)}
                            </td>

                            <td>
                              ₹ {row.principal.toFixed(2)}
                            </td>

                            <td>
                              ₹ {row.interest.toFixed(2)}
                            </td>

                            <td>
                              ₹ {row.balance.toFixed(2)}
                            </td>
                          </tr>
                        ))}

                      </tbody>

                    </table>
                  </div>

                </div>
              )}

            </div>

        

          </div>

        </div>

        {/* SEO CONTENT */}
        <div className="col-lg-10 mx-auto mt-5">

          <h2 className="fw-bold mb-3">What is EMI?</h2>
          <p>
            EMI stands for Equated Monthly Installment. It is the fixed amount
            you pay to your bank or lender every month until your loan is fully
            repaid. Each EMI payment consists of two parts — a portion goes
            toward repaying the original loan amount (principal), and the
            remaining portion covers the interest charged by the lender. In the
            early months of a loan, most of the EMI goes toward interest. As
            time passes, the principal component increases and the interest
            component decreases. This is known as an amortizing loan structure.
          </p>

          <h2 className="fw-bold mt-5 mb-3">EMI Formula Explained</h2>
          <p>
            The standard EMI formula used by all Indian banks and financial
            institutions is: EMI = [P × R × (1+R)^N] / [(1+R)^N – 1], where P
            is the principal loan amount, R is the monthly interest rate (annual
            rate divided by 12), and N is the total number of monthly
            installments. Our calculator uses this exact formula to give you
            accurate results that match what your bank will quote.
          </p>

          <h2 className="fw-bold mt-5 mb-3">How to Use This EMI Calculator</h2>
          <ol>
            <li className="mb-2">
              <strong>Select your loan type</strong> — choose Home Loan, Car
              Loan, or Personal Loan. The interest rate will be pre-filled with
              a typical rate for that loan type.
            </li>
            <li className="mb-2">
              <strong>Enter the loan amount</strong> — type the total amount you
              wish to borrow in rupees.
            </li>
            <li className="mb-2">
              <strong>Enter the tenure</strong> — provide the repayment period
              in months. For example, 5 years = 60 months, 20 years = 240
              months.
            </li>
            <li className="mb-2">
              <strong>Adjust the interest rate</strong> — use the slider to set
              the annual interest rate offered by your bank.
            </li>
            <li className="mb-2">
              <strong>Click Calculate EMI</strong> — your monthly EMI, total
              interest payable, and a full amortization schedule will appear
              instantly.
            </li>
            <li className="mb-2">
              <strong>Download PDF</strong> — save the complete repayment
              schedule as a PDF for your records.
            </li>
          </ol>

          <h2 className="fw-bold mt-5 mb-3">
            Home Loan vs Car Loan vs Personal Loan EMI
          </h2>
          <p>
            Different loan types have different interest rates and typical
            tenures, which significantly affects your monthly EMI. Home loans
            in India typically carry interest rates between 8% and 9.5% and
            can have tenures of up to 30 years, making them the most affordable
            in terms of monthly outgo despite large loan amounts. Car loans
            usually come at 9% to 11% with tenures of 1 to 7 years. Personal
            loans are the most expensive, typically ranging from 11% to 24%
            with shorter tenures of 1 to 5 years. Use this calculator to
            compare the EMI across all three types before making a borrowing
            decision.
          </p>

          <h2 className="fw-bold mt-5 mb-3">
            Tips to Reduce Your EMI Burden
          </h2>
          <p>
            There are several ways to reduce the EMI you pay each month. Making
            a larger down payment reduces the principal amount, which directly
            lowers your EMI. Negotiating a lower interest rate with your bank,
            even a reduction of 0.5%, can save thousands of rupees over a long
            tenure. Opting for a longer tenure also reduces monthly EMI, though
            it increases total interest paid. Making part-prepayments during the
            loan tenure reduces the outstanding principal and can significantly
            cut down the total interest cost. Use our amortization schedule to
            see how each change affects your overall repayment.
          </p>

          <h2 className="fw-bold mt-5 mb-3">Frequently Asked Questions</h2>

          <div className="mb-4">
            <h5>What is a good EMI-to-income ratio?</h5>
            <p>
              Financial experts recommend keeping your total EMI obligations
              below 40% of your monthly take-home income. For example, if you
              earn ₹60,000 per month, your combined EMIs across all loans should
              ideally not exceed ₹24,000. Staying within this limit ensures you
              have enough left for daily expenses, savings, and emergencies
              without financial stress.
            </p>
          </div>

          <div className="mb-4">
            <h5>Does increasing tenure reduce EMI?</h5>
            <p>
              Yes, a longer loan tenure means smaller monthly EMI payments.
              However, the trade-off is that you pay significantly more
              interest over time. For example, a ₹20 lakh loan at 9% for
              10 years has an EMI of about ₹25,335 and a total interest cost
              of around ₹10.4 lakh. The same loan over 20 years has an EMI
              of ₹17,995 but total interest of ₹23.2 lakh. Use this
              calculator to find the right balance between affordable EMI
              and reasonable interest cost.
            </p>
          </div>

          <div className="mb-4">
            <h5>What is the difference between flat rate and reducing balance EMI?</h5>
            <p>
              In a flat rate loan, interest is calculated on the original
              principal throughout the entire tenure regardless of how much
              you have repaid. In a reducing balance loan — which is the
              standard method used by most Indian banks — interest is charged
              only on the outstanding loan balance each month. This means
              your interest cost reduces every month as you repay. Our
              calculator uses the reducing balance method, which is what
              your actual bank EMI will be based on.
            </p>
          </div>

          <div className="mb-4">
            <h5>Can I use this calculator for home loans above ₹1 crore?</h5>
            <p>
              Yes, our EMI calculator works for any loan amount. Simply enter
              the full loan amount in rupees. For a ₹1 crore home loan at 8.5%
              for 20 years, for example, the EMI would be approximately
              ₹86,782 per month. There is no upper limit on the loan amount
              you can calculate.
            </p>
          </div>

          <div className="mb-4">
            <h5>Can I download the EMI report?</h5>
            <p>
              Yes. After calculating your EMI, click the "Download PDF Report"
              button to save a complete month-by-month amortization schedule
              as a PDF file. The report shows each month's EMI, principal
              paid, interest paid, and remaining balance. This is useful for
              personal financial records or sharing with a financial advisor
              or bank manager.
            </p>
          </div>

          <div className="mt-5 p-4 bg-light rounded-4">
            <h3 className="fw-bold mb-3">Related Financial Tools</h3>
            <div className="d-flex flex-wrap gap-3">
              <a href="/loan-eligibility" className="btn btn-outline-primary">Loan Eligibility</a>
              <a href="/tax-calculator" className="btn btn-outline-primary">Tax Calculator</a>
              <a href="/percentage-calculator" className="btn btn-outline-primary">Percentage Calculator</a>
            </div>
          </div>

        </div>

      </div>

      <Footer />
    </>
  );
}